import { z } from "zod";

export const DIAGNOSTIC_WINDOW_MS = 60_000;
const MAX_CONSOLE_ENTRIES = 120;
const MAX_ERROR_ENTRIES = 40;
const MAX_MESSAGE_LENGTH = 500;

export enum DiagnosticLogLevel {
	Log = "log",
	Info = "info",
	Warn = "warn",
	Error = "error",
}

export enum DiagnosticErrorKind {
	WindowError = "window_error",
	UnhandledRejection = "unhandled_rejection",
	ConsoleError = "console_error",
}

export const diagnosticConsoleEntrySchema = z.object({
	timestamp: z.string(),
	timestampMs: z.number(),
	level: z.nativeEnum(DiagnosticLogLevel),
	message: z.string(),
});

export type DiagnosticConsoleEntry = z.infer<
	typeof diagnosticConsoleEntrySchema
>;

export const diagnosticErrorEntrySchema = z.object({
	timestamp: z.string(),
	timestampMs: z.number(),
	kind: z.nativeEnum(DiagnosticErrorKind),
	message: z.string(),
	stack: z.string().nullable(),
});

export type DiagnosticErrorEntry = z.infer<typeof diagnosticErrorEntrySchema>;

export const diagnosticSnapshotSchema = z.object({
	windowMs: z.number(),
	capturedAt: z.string(),
	consoleEntries: z.array(diagnosticConsoleEntrySchema),
	errorEntries: z.array(diagnosticErrorEntrySchema),
});

export type DiagnosticSnapshot = z.infer<typeof diagnosticSnapshotSchema>;

const SENSITIVE_PATTERN =
	/(bearer\s+[a-zA-Z0-9._~+/-]+=*|eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+|"(?:access_token|id_token|refresh_token|authorization|password|secret|cookie)"\s*:\s*"[^"]*")/gi;

export function redactSensitiveText(input: string): string {
	return input.replace(SENSITIVE_PATTERN, "[REDACTED]");
}

function formatArg(arg: unknown): string {
	if (typeof arg === "string") {
		return arg;
	}
	if (
		typeof arg === "number" ||
		typeof arg === "boolean" ||
		arg === null ||
		arg === undefined
	) {
		return String(arg);
	}
	if (arg instanceof Error) {
		return arg.stack
			? `${arg.name}: ${arg.message} (${arg.stack.split("\n")[1]?.trim() ?? ""})`
			: `${arg.name}: ${arg.message}`;
	}
	try {
		return JSON.stringify(arg);
	} catch {
		return "[Unserializable]";
	}
}

export function formatConsoleArgs(args: unknown[]): string {
	const raw = args.map(formatArg).join(" ");
	const redacted = redactSensitiveText(raw);
	if (redacted.length <= MAX_MESSAGE_LENGTH) {
		return redacted;
	}
	return `${redacted.slice(0, MAX_MESSAGE_LENGTH)}…`;
}

interface RingBufferState {
	installed: boolean;
	inHook: boolean;
	consoleEntries: DiagnosticConsoleEntry[];
	errorEntries: DiagnosticErrorEntry[];
	restoreFns: Array<() => void>;
}

const bufferState: RingBufferState = {
	installed: false,
	inHook: false,
	consoleEntries: [],
	errorEntries: [],
	restoreFns: [],
};

function pruneExpired(nowMs: number): void {
	const cutoff = nowMs - DIAGNOSTIC_WINDOW_MS;
	while (
		bufferState.consoleEntries.length > 0 &&
		(bufferState.consoleEntries[0]?.timestampMs ?? 0) < cutoff
	) {
		bufferState.consoleEntries.shift();
	}
	while (
		bufferState.errorEntries.length > 0 &&
		(bufferState.errorEntries[0]?.timestampMs ?? 0) < cutoff
	) {
		bufferState.errorEntries.shift();
	}
}

export function recordConsoleEntry(
	level: DiagnosticLogLevel,
	args: unknown[],
	nowMs = Date.now(),
): void {
	if (bufferState.inHook) return;
	bufferState.inHook = true;
	try {
		pruneExpired(nowMs);
		const message = formatConsoleArgs(args);
		if (!message) return;

		const timestamp = new Date(nowMs).toISOString();
		bufferState.consoleEntries.push({
			timestamp,
			timestampMs: nowMs,
			level,
			message,
		});
		if (bufferState.consoleEntries.length > MAX_CONSOLE_ENTRIES) {
			bufferState.consoleEntries.shift();
		}

		if (level === DiagnosticLogLevel.Error) {
			const firstError = args.find((item) => item instanceof Error);
			const stack =
				firstError instanceof Error && firstError.stack
					? redactSensitiveText(firstError.stack.slice(0, MAX_MESSAGE_LENGTH))
					: null;
			bufferState.errorEntries.push({
				timestamp,
				timestampMs: nowMs,
				kind: DiagnosticErrorKind.ConsoleError,
				message,
				stack,
			});
			if (bufferState.errorEntries.length > MAX_ERROR_ENTRIES) {
				bufferState.errorEntries.shift();
			}
		}
	} finally {
		bufferState.inHook = false;
	}
}

export function recordErrorEntry(
	kind: DiagnosticErrorKind,
	message: string,
	stack: string | null = null,
	nowMs = Date.now(),
): void {
	if (bufferState.inHook) return;
	bufferState.inHook = true;
	try {
		pruneExpired(nowMs);
		const cleanMessage = redactSensitiveText(message).slice(
			0,
			MAX_MESSAGE_LENGTH,
		);
		const cleanStack = stack
			? redactSensitiveText(stack).slice(0, MAX_MESSAGE_LENGTH)
			: null;
		bufferState.errorEntries.push({
			timestamp: new Date(nowMs).toISOString(),
			timestampMs: nowMs,
			kind,
			message: cleanMessage,
			stack: cleanStack,
		});
		if (bufferState.errorEntries.length > MAX_ERROR_ENTRIES) {
			bufferState.errorEntries.shift();
		}
	} finally {
		bufferState.inHook = false;
	}
}

export function getDiagnosticSnapshot(nowMs = Date.now()): DiagnosticSnapshot {
	pruneExpired(nowMs);
	return {
		windowMs: DIAGNOSTIC_WINDOW_MS,
		capturedAt: new Date(nowMs).toISOString(),
		consoleEntries: [...bufferState.consoleEntries],
		errorEntries: [...bufferState.errorEntries],
	};
}

export function initDiagnosticRingBuffer(): void {
	if (bufferState.installed || typeof window === "undefined") {
		return;
	}
	bufferState.installed = true;

	const levels: Array<{
		method: "log" | "info" | "warn" | "error";
		level: DiagnosticLogLevel;
	}> = [
		{ method: "log", level: DiagnosticLogLevel.Log },
		{ method: "info", level: DiagnosticLogLevel.Info },
		{ method: "warn", level: DiagnosticLogLevel.Warn },
		{ method: "error", level: DiagnosticLogLevel.Error },
	];

	for (const { method, level } of levels) {
		const original = console[method].bind(console);
		console[method] = (...args: unknown[]) => {
			recordConsoleEntry(level, args);
			original(...args);
		};
		bufferState.restoreFns.push(() => {
			console[method] = original;
		});
	}

	const onWindowError = (event: ErrorEvent) => {
		const err = event.error;
		const stack = err instanceof Error ? (err.stack ?? null) : null;
		const msg =
			event.message || (err instanceof Error ? err.message : "Window error");
		recordErrorEntry(DiagnosticErrorKind.WindowError, msg, stack);
	};

	const onUnhandledRejection = (event: PromiseRejectionEvent) => {
		const reason = event.reason;
		const msg =
			reason instanceof Error
				? `${reason.name}: ${reason.message}`
				: formatConsoleArgs([reason]);
		const stack = reason instanceof Error ? (reason.stack ?? null) : null;
		recordErrorEntry(DiagnosticErrorKind.UnhandledRejection, msg, stack);
	};

	window.addEventListener("error", onWindowError);
	window.addEventListener("unhandledrejection", onUnhandledRejection);
	bufferState.restoreFns.push(() => {
		window.removeEventListener("error", onWindowError);
		window.removeEventListener("unhandledrejection", onUnhandledRejection);
	});
}

export function resetDiagnosticRingBufferForTests(): void {
	for (const restore of bufferState.restoreFns) {
		restore();
	}
	bufferState.restoreFns = [];
	bufferState.consoleEntries = [];
	bufferState.errorEntries = [];
	bufferState.installed = false;
	bufferState.inHook = false;
}
